<script setup lang="ts">
import InputText from 'primevue/inputtext'

interface Props {
  modelValue?: string | null
  type?: string
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  size?: 'small' | 'medium'
  /** Hard cap on the number of characters the user can enter. */
  maxlength?: number
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  type: 'text',
  disabled: false,
  invalid: false,
  size: 'medium',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur', event: FocusEvent): void
}>()

function handleUpdate(value: string | undefined) {
  emit('update:modelValue', value ?? '')
}
</script>

<template>
  <InputText
    :class="['base-input', `base-input--${size}`]"
    :modelValue="props.modelValue ?? ''"
    :type="type"
    :placeholder="placeholder"
    :disabled="disabled"
    :invalid="invalid"
    :maxlength="maxlength"
    @update:modelValue="handleUpdate"
    @blur="(e) => emit('blur', e)"
  />
</template>

<style scoped>
/* `.p-inputtext` is added by PrimeVue on the root <input>; scoped data-v
   attribute applies cleanly because both classes share that root. */
.base-input.p-inputtext {
  border-radius: var(--p-border-radius-md);
  padding: 0 0.75rem;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.base-input.p-inputtext:focus,
.base-input.p-inputtext:focus-visible {
  border-color: var(--p-primary-500);
  box-shadow: 0 0 0 4px rgba(103, 85, 251, 0.12);
  outline: none;
}

.base-input.p-inputtext.p-invalid:focus,
.base-input.p-inputtext.p-invalid:focus-visible {
  border-color: var(--p-danger-500);
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.12);
}

.base-input--medium.p-inputtext {
  height: 42px;
}

.base-input--small.p-inputtext {
  height: 32px;
  font-size: 0.75rem;
}
</style>
